/**
 * SignalPath AI Exposure — deterministic task-level scorer (ADR-021).
 *
 * This is the FALLBACK tier, and under ADR-023 it is what most people see:
 * live model scoring requires a signed-in user, so anonymous visitors run
 * entirely on this. ADR-021 is explicit that fallback quality IS product
 * quality — this is not a stub standing in for the real thing.
 *
 * Four axes, per ADR-021:
 *   digitality      can this be done entirely through a screen?
 *   judgment        must someone OWN the outcome?
 *   coordination    does it need context held across people and time?
 *   premium         will anyone still pay EXTRA once a free alternative exists?
 *
 * The fourth is the one most exposure tools miss. Choudary: the task usually
 * survives; the price does not. A task AI cannot fully do but nobody pays extra
 * for is a MORE urgent signal than one AI can do — so where premium disagrees
 * with the automation axes, we surface the disagreement instead of averaging.
 */

/**
 * Term matching is STEM-PREFIX and word-anchored: /\bapprov/ matches "approve",
 * "approves", "approving", "approval". Two separate bugs came from getting this
 * wrong, in opposite directions:
 *
 *   too loose — a bare "operate" matched "operate data entry devices" and made
 *               the most screen-bound occupation in the dataset look physical;
 *   too tight — the literal "approve" missed "approving expenditures", so
 *               "Perform administrative functions ... approving expenditures,
 *               enforcing rules" scored judgment 2 and came out fully AUTOMATED.
 *
 * Anchoring at word start with an explicit stem fixes both: it catches the
 * inflections and refuses the substring buried inside another word.
 */
const compile = (terms) =>
  terms.map((t) => new RegExp(`\\b${t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`, "i"));

/** How many distinct terms fire — not merely whether any did. */
const hits = (t, patterns) => patterns.reduce((n, re) => n + (re.test(t) ? 1 : 0), 0);

const DIGITAL = compile(["data", "report", "document", "spreadsheet", "email", "record", "database",
  "software", "code", "program", "compil", "calculat", "schedul", "file", "correspondence",
  "analyz", "analys", "review", "prepar", "draft", "updat", "enter ", "process", "online",
  "keyboard", "input", "scan", "computer", "system", "form", "spreadsheet", "digital", "electronic"]);

// Deliberately SPECIFIC. See the note above on "operate".
const PHYSICAL = compile(["install", "repair", "operate machin", "operate equipment", "operate vehicle",
  "machinery", "patient", "clean", "assembl", "driv", "lift", "physically", "on-site",
  "specimen", "vehicle", "construct", "weld", "excavat", "plumb", "wiring", "hand tools",
  "bedside", "surgical", "laboratory bench", "inspect site", "field work"]);

const JUDGMENT = compile(["decid", "determin", "approv", "evaluat", "recommend", "judgment",
  "judgement", "negotiat", "resolv", "prioriti", "strateg", "diagnos",
  "assess", "select", "authoriz", "authoris", "responsible for", "accountable", "advis",
  "interpret", "design", "plan", "budget", "bid", "contract", "estimat", "policy", "policies",
  "risk", "trade-off", "govern", "arbitrat", "adjudicat", "set goals", "establish"]);

const COORDINATION = compile(["coordinat", "collaborat", "liaise", "stakeholder", "team", "direct",
  "supervis", "manag", "confer", "consult", "negotiat", "communicat", "represent",
  "partner", "across departments", "cross-functional", "lead", "present", "explain",
  "client", "customer", "brief", "facilitat", "meet with", "staff", "personnel"]);

// Language that marks work whose price has already collapsed: routine production
// of artefacts a model now emits for free.
//
// "standard" USED to be on this list and it was wrong in the most damaging way
// available: "Develop or implement policies, standards, or procedures" — the
// single most judgment-heavy line in a manager's task set — was flagged PREMIUM
// GONE because a noun meaning "a norm we set" collided with an adjective meaning
// "nothing special". Only the phrases that unambiguously mean routine belong.
const COMMODITISED = compile(["routine", "transcrib", "enter data", "data entry",
  "maintain records", "generate reports", "summariz", "summaris", "reformat", "proofread",
  "record keeping", "filing", "boilerplate", "standard form", "standard template",
  "prepare routine", "draft correspondence", "compile lists", "collate"]);

const clamp = (n) => Math.max(0, Math.min(10, n));

/**
 * Score one task statement on the four axes.
 *
 * Each axis ACCUMULATES over the terms that fire rather than flipping on the
 * first one. Flat flags gave every task one of about four possible signatures,
 * so a twelve-task role rendered as "dig 8 · jud 2 · coo 2 · prem 0.6" three
 * times over — visibly identical rows under a heading promising "scored task by
 * task". The claim has to be true on the page, not just in the architecture.
 */
export function scoreTask(taskText) {
  const t = String(taskText || "").toLowerCase();

  const dHits = hits(t, DIGITAL);
  const pHits = hits(t, PHYSICAL);
  let digitality = 4.5 + Math.min(4, dHits * 1.3) - Math.min(7, pHits * 2.6);
  digitality = clamp(digitality);

  const cmHits = hits(t, COMMODITISED);
  const jHits = hits(t, JUDGMENT);
  let judgment = 1.5 + Math.min(7, jHits * 1.7) - cmHits * 1.4;
  judgment = clamp(judgment);

  const coHits = hits(t, COORDINATION);
  let coordination = 1.5 + Math.min(7.5, coHits * 1.9);
  coordination = clamp(coordination);

  // Premium durability: judgment and coordination protect the price;
  // commoditised language destroys it regardless of how hard the task is.
  let premium = 5 + (judgment - 5) * 0.5 + (coordination - 5) * 0.3;
  if (cmHits) premium -= 2 + cmHits;
  if (digitality >= 8 && judgment <= 3) premium -= 2;
  premium = clamp(premium);

  const status =
    digitality <= 3 ? "durable"
    : judgment >= 6 || coordination >= 6 ? "augmented"
    : "automated";

  // Exposure is high when the work is digital, unowned, and unpaid-for — but
  // digitality GATES the rest rather than merely contributing to it. Work that
  // cannot be done through a screen is not exposed however routine it is; an
  // additive formula scored electricians "Moderate" purely on low judgment,
  // which is a scoring artefact, not a finding.
  const base =
    digitality * 0.35 + (10 - judgment) * 0.35 + (10 - coordination) * 0.10 + (10 - premium) * 0.20;
  // The gate saturates once work is genuinely screen-bound (~7), rather than
  // damping the top end. Below that it falls away fast: physical work is not
  // exposed, however routine.
  const gate = Math.max(0.25, Math.min(1, 0.25 + 0.107 * digitality));
  const exposure = clamp(base * gate);

  return {
    task: taskText,
    digitality: round(digitality),
    judgment: round(judgment),
    coordination: round(coordination),
    premium: round(premium),
    exposure: round(exposure),
    status,
    // The finding worth surfacing: AI can't fully do it, but the price is gone.
    premiumWarning: premium <= 3 && digitality < 8,
  };
}

const round = (n) => Math.round(n * 10) / 10;

/**
 * Roll up a person from their work.
 *
 * Two changes from scoring an occupation flat, and both matter:
 *
 * 1. **Tasks are weighted by O*NET importance.** An unweighted mean says the
 *    twelfth-most-important thing you do counts as much as the first. It does
 *    not, and the ratings to say so were in the data all along — we were reading
 *    whichever twelve rows the CSV happened to list first.
 *
 * 2. **Confirmed activities outrank the occupation's own list.** Everything a
 *    person confirmed during refinement (ADR-033) enters at maximum weight,
 *    because they told us they do it — which is better evidence than a national
 *    average for a code we inferred. Until this existed, every holder of an
 *    occupation code received an identical exposure score, so the only thing
 *    personal about the result was the match. Two IT project managers do
 *    different work; now they get different numbers.
 *
 * The occupation's tasks remain in the roll-up rather than being replaced. Four
 * confirmed activities are not a complete picture of a job, and dropping the
 * rest would trade a generic answer for a thin one.
 */
export function scoreOccupation(tasks, { limit = 12, confirmed = [] } = {}) {
  const fromOccupation = (tasks || []).slice(0, limit).map((t) => ({
    ...scoreTask(t.task || t),
    weight: typeof t.importance === "number" ? t.importance : 3,
    source: "occupation",
  }));

  // Weight 5 is the top of O*NET's importance scale. A person saying "yes, I do
  // this" is the strongest signal available about their own work.
  const fromPerson = (confirmed || []).map((a) => ({
    ...scoreTask(a),
    weight: 5,
    source: "confirmed",
  }));

  const scored = [...fromPerson, ...fromOccupation];
  if (!scored.length) return null;

  const totalWeight = scored.reduce((s2, x) => s2 + x.weight, 0) || 1;
  const mean = (k) => round(scored.reduce((s2, x) => s2 + x[k] * x.weight, 0) / totalWeight);
  const counts = scored.reduce((a, x) => ((a[x.status] = (a[x.status] || 0) + 1), a), {});

  return {
    tasks: scored.sort((a, b) => b.exposure - a.exposure),
    exposure: mean("exposure"),
    digitality: mean("digitality"),
    judgment: mean("judgment"),
    coordination: mean("coordination"),
    premium: mean("premium"),
    counts,
    method: fromPerson.length ? "deterministic-v1+confirmed" : "deterministic-v1",
    personalised: fromPerson.length,
    premiumWarnings: scored.filter((s2) => s2.premiumWarning).length,
  };
}

export const exposureLabel = (s) => (s >= 7 ? "High" : s >= 4 ? "Moderate" : "Low");
